/**
 * Tray Gateway API client (ADR-0042).
 *
 * Mirrors `backend/src/hub/admin/routes/gateways.py`. A gateway is the
 * cloud-side identity of an on-prem edge connector agent that reaches
 * firewalled databases / files. Slice 0 is the entity lifecycle: list /
 * create / rename / **revoke** (the kill-switch) / delete. Enrollment +
 * the live transport land in later slices.
 */
import { api } from "./api";


/** enrolling → an agent has been registered but not yet connected.
 *  online/offline are set by the (future) heartbeat; revoked is terminal. */
export type GatewayStatus = "enrolling" | "online" | "offline" | "revoked";

export interface Gateway {
  id: string;
  name: string;
  description: string | null;
  status: GatewayStatus;
  /** Bumped on revoke — the kill-switch the agent's cert is checked against. */
  version: number;
  agent_version: string | null;
  last_seen_at: string | null;
  created_at: string;
}

export interface GatewayBody {
  name: string;
  description?: string | null;
}

/** One-time enrollment material — the token is shown ONCE; paste it into the
 *  on-prem agent (with the CA the agent pins). ADR-0042 slice 1. */
export interface EnrollToken {
  token: string;
  expires_at: string;
  ca_pem: string;
}


/** A connection NAME the on-prem agent holds (never the DSN). ADR-0042 slice 7. */
export interface GatewayRemote {
  ref: string;
}

/** Result of the remote-ref picker query. `online: false` = no agent connected (or
 *  revoked) — the picker shows "agent offline" instead of a bare empty list. */
export interface GatewayRemotes {
  online: boolean;
  remotes: GatewayRemote[];
}


/** One operator health check shown in the gateway detail drawer (ADR-0042). */
export interface DiagnosticCheck {
  key: string;
  ok: boolean;
  label: string;
  detail: string | null;
}

/** Everything needed to verify a gateway is healthy, in one call. */
export interface GatewayDiagnostics {
  id: string;
  name: string;
  status: GatewayStatus;
  version: number;
  agent_version: string | null;
  last_seen_at: string | null;
  enrolled_at: string | null;
  created_at: string;
  created_by: string;
  cert_fingerprint_short: string | null;
  agent_connected: boolean;
  enroll_pending: boolean;
  remotes_online: boolean;
  remotes: GatewayRemote[];
  checks: DiagnosticCheck[];
}


export const gatewaysApi = {
  list: () => api.get<Gateway[]>("/gateways"),

  get: (id: string) => api.get<Gateway>(`/gateways/${id}`),

  /** The connection names the gateway's agent holds — for the credential remote-ref picker. */
  remotes: (id: string) => api.get<GatewayRemotes>(`/gateways/${id}/remotes`),

  /** Operator debugging snapshot (identity + live agent connectivity + health checks). */
  diagnostics: (id: string) => api.get<GatewayDiagnostics>(`/gateways/${id}/diagnostics`),

  create: (body: GatewayBody) => api.post<Gateway>("/gateways", body),

  update: (id: string, body: GatewayBody) =>
    api.patch<Gateway>(`/gateways/${id}`, body),

  revoke: (id: string) => api.post<Gateway>(`/gateways/${id}/revoke`, {}),

  /** Mint a one-time enrollment token (mTLS). Returned ONCE — show it, don't store it. */
  enrollToken: (id: string) => api.post<EnrollToken>(`/gateways/${id}/enroll-token`, {}),

  remove: (id: string) => api.delete<void>(`/gateways/${id}`),

  /**
   * DEV ONLY (HUB_DEV_MODE) — wire a gateway to a local DSN via an in-process stub agent,
   * so a gateway-bound Source is testable from the UI before the real on-prem agent exists
   * (ADR-0042 §10). 404 in prod.
   */
  devStub: (id: string, remote_ref: string, dsn: string) =>
    api.post<void>(`/gateways/${id}/dev-stub`, { remote_ref, dsn }),
};
